import { colors } from '@atlaskit/theme';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { DateGroup, DateGroupItems, groupTimedItemsByGroup, TimedItem } from '../util/date';

const GroupedListContainer = styled.div`
  margin-top: 5px;

  > :last-child {
    margin-bottom: 10px;
  }
`;

const Group = styled.div`
  margin-top: 16px;

  > * + * {
    margin-top: 8px;
  }
`;

const Header = styled.h2`
  color: ${colors.N200};
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 10px;
`;

export interface GroupedListProps<AnyItem extends TimedItem> {
  data: AnyItem[];
  renderItem: (item: AnyItem & { fullIndex: number }, group: DateGroup) => JSX.Element;
}

function withIndex<AnyItem extends TimedItem>(
  groupedData: DateGroupItems<AnyItem>[],
): DateGroupItems<AnyItem & { fullIndex: number }>[] {
  let fullIndex = 0;
  return groupedData.map(group => ({
    ...group,
    items: group.items.map(item => ({
      ...item,
      fullIndex: fullIndex++,
    })),
  }));
}

class GroupedList<AnyItem extends TimedItem> extends React.Component<GroupedListProps<AnyItem>> {
  render() {
    const { data } = this.props;

    const groupedData = groupTimedItemsByGroup(data);
    const indexedGroupedData = withIndex(groupedData);

    return <GroupedListContainer>{indexedGroupedData.map(this.renderEntry)}</GroupedListContainer>;
  }
  renderEntry = (dateGroup: DateGroupItems<AnyItem & { fullIndex: number }>) => {
    const { time, items, group } = dateGroup;
    const { renderItem } = this.props;

    return (
      <Group key={time.toISOString()}>
        <Header>
          <FormattedMessage {...messages[group]} />
        </Header>
        {items.map(item => renderItem(item, group))}
      </Group>
    );
  };
}

export default GroupedList;

const messages = defineMessages<DateGroup>({
  older: {
    defaultMessage: 'Older',
    description: 'section heading for items older than the past month',
    id: 'uchi.groupedList.older',
  },
  pastMonth: {
    defaultMessage: 'In the past month',
    description: 'section heading for items belonging to the past month (and before the past week)',
    id: 'uchi.groupedList.pastMonth',
  },
  pastWeek: {
    defaultMessage: 'In the past week',
    description: 'section heading for items belonging to the past week (and before yesterday)',
    id: 'uchi.groupedList.pastWeek',
  },
  today: {
    defaultMessage: 'Today',
    description: 'section heading for items belonging to the current day',
    id: 'uchi.groupedList.today',
  },
  yesterday: {
    defaultMessage: 'Yesterday',
    description: 'section heading for items belonging to the previous day',
    id: 'uchi.groupedList.yesterday',
  },
});
